import { sensorDataAggregatedSchema } from '@si/types';
import { get, objectToQueryParams } from '@si/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { queryKeys } from './queryKeys';

const responseSchema = z.object({
  sensorDataAggregated: z.array(sensorDataAggregatedSchema),
});

interface Params {
  projectId: string;
  from: string;
  to: string;
  enabled?: boolean;
  includeStatistics?: boolean;
  noRetry?: boolean;
  sensorIds?: string;
}

export function useSensorDataAggregated({
  projectId,
  from,
  to,
  enabled = true,
  includeStatistics = false,
  noRetry = false,
  sensorIds,
}: Params) {
  const queryClient = useQueryClient();
  const completeDatasetQueryKey = queryKeys.getSensorDataAggregated(
    projectId,
    from,
    to,
    includeStatistics
  );
  const hasCompleteDataset = queryClient.getQueryData(completeDatasetQueryKey) != null;
  const requestedSensorIds = hasCompleteDataset ? undefined : sensorIds;
  const queryKey = queryKeys.getSensorDataAggregated(
    projectId,
    from,
    to,
    includeStatistics,
    requestedSensorIds
  );
  const url = `/projects/${projectId}/sensor-data/aggregated${objectToQueryParams({
    from,
    includeStatistics: includeStatistics || undefined,
    sensorIds: requestedSensorIds,
    to,
  })}`;

  const { data, error, isLoading } = useQuery({
    queryKey,
    throwOnError: false,
    queryFn: () => get({ url, schema: responseSchema }),
    enabled: enabled && !!projectId && !!from && !!to,
    ...(noRetry ? { retry: false } : {}),
    staleTime: 5 * 60 * 1000,
  });

  return {
    sensorDataAggregated: data?.sensorDataAggregated || [],
    error,
    isLoading,
  };
}
